// Bileli · shared/api — zod schemas mirroring the backend OpenAPI DTOs (the fields the fork reads; zod
// strips unknown keys, so additive backend changes stay non-breaking while shape drift fails loudly via
// parseApi). ⚠ NULL-TOLERANCE RULE: the Ktor backend serializes absent values as explicit `null`
// (e.g. `"questions": null`) — every non-required field is `.nullish()`, NEVER bare `.optional()`.

import { z } from 'zod';

// ── media ─────────────────────────────────────────────────────────────────────────────────────────────
export const MediaAssetSchema = z.object({
  id: z.string(),
  kind: z.string().nullish(),
  state: z.string().nullish(),
  contentType: z.string().nullish(),
});

export const UploadTicketSchema = z.discriminatedUnion('protocol', [
  z.object({
    protocol: z.literal('S3_PUT'),
    asset: MediaAssetSchema,
    uploadExpiresInSec: z.number().nullish(),
    uploadUrl: z.string(),
  }),
  z.object({
    protocol: z.literal('CF_DIRECT_FORM'),
    asset: MediaAssetSchema,
    uploadExpiresInSec: z.number().nullish(),
    uploadUrl: z.string(),
  }),
  z.object({
    protocol: z.literal('BUNNY_TUS'),
    asset: MediaAssetSchema,
    uploadExpiresInSec: z.number().nullish(),
    creationUrl: z.string(),
    tusVersion: z.string(),
    libraryId: z.number(),
    videoGuid: z.string(),
    authorizationSignature: z.string(),
    authorizationExpire: z.number(),
    uploadMetadataBase64: z.string(),
  }),
]);

export const FinalizeResponseSchema = z.object({ asset: MediaAssetSchema });

// ── catalog (learn) ───────────────────────────────────────────────────────────────────────────────────
// Declared before CatalogCourseSchema because that schema now references it (the catalog LIST carries
// categories too — CourseDto marks them required).
export const CategorySchema = z.object({
  key: z.string(),
  facet: z.enum(['SUBJECT', 'GOAL']),
  labelKy: z.string(),
  labelRu: z.string(),
});

export const CatalogCourseSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string().nullish(),
  language: z.string().nullish(),
  // Bileli: `avatarUrl` is the org's logo — the same media/variant as the provider profile's logoUrl,
  // so the two surfaces share one cached image (backend round 2026-07-27). Null is the common case.
  provider: z.object({
    name: z.string().nullish(), slug: z.string().nullish(), avatarUrl: z.string().nullish(),
  }).nullish(),
  // Bileli: course difficulty, tri-state on the wire (absent/null = the teacher never set one).
  // Plain string, not an enum — a future level must degrade to "no pill", never fail the parse.
  level: z.string().nullish(),
  // Bileli: the backend has always sent these on CourseDto (required there) — we simply never parsed
  // them, and zod strips unknown keys. The catalog card needs the subject for its cover tag.
  categories: z.array(CategorySchema).nullish(),
  ratingAvg: z.number().nullish(),
  ratingCount: z.number().nullish(),
  coverImageUrl: z.string().nullish(),
  cohorts: z.array(z.object({ priceMinor: z.number().nullish() }).loose()).nullish(),
  // Bileli 2026-07-28: the course's storefront topic — see TeacherCourseDetailSchema. DETAIL reads only;
  // list endpoints serve null by contract, so the catalog GRID never gets these and must not expect them.
  // Bileli 2026-07-29: SHOWROOM REMOVED (backend dropped it — showroom-removal-web-integration.md).
  // showroomUsername: z.string().nullish(),
  // showroomTopicId: z.number().nullish(),
});

export const CatalogPageSchema = z.object({
  items: z.array(CatalogCourseSchema).nullish(),
  nextCursor: z.string().nullish(),
});

// ── programs / provider profile (PR #184, V49–V51) ───────────────────────────────────────────────────
// Enums (`state`, `kind`) stay z.string() here — unknown-tolerant decode happens in the module mappers
// (catalog.api ternary precedent), so a future backend enum value degrades instead of failing parse.
const ProgramProviderSchema = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string(),
  kind: z.string(), // PERSONAL | INSTITUTIONAL
  avatarUrl: z.string().nullish(), // Bileli: the org logo (round 2026-07-27) — see CatalogCourseSchema
}).loose();

export const ProgramSummarySchema = z.object({
  id: z.string(),
  slug: z.string(),
  provider: ProgramProviderSchema,
  titleKy: z.string(),
  titleRu: z.string(),
  taglineKy: z.string().nullish(),
  taglineRu: z.string().nullish(),
  eyebrowKy: z.string().nullish(),
  eyebrowRu: z.string().nullish(),
  coverUrl: z.string().nullish(),
  featured: z.boolean(),
  featuredOrder: z.number().nullish(),
  state: z.string(), // PUBLISHED | ARCHIVED | DRAFT (reserved, never produced yet)
  courseCount: z.number(), // PUBLISHED courses only
  ratingAvg: z.number().nullish(), // pooled over published courses; null = no reviews (render nothing)
  ratingCount: z.number(), // int64 here vs int32 on the org profile — accepted wire drift, don't "fix"
});
export const ProgramSummariesSchema = z.array(ProgramSummarySchema);

// ProgramDto = summary + ordered courses + timestamps. Embedded courses reuse the public course shape;
// `state` is decoded when present so the studio can chip not-yet-published members.
export const ProgramSchema = ProgramSummarySchema.extend({
  courses: z.array(CatalogCourseSchema.extend({ state: z.string().nullish() }).loose()).nullish(),
  createdAt: z.string().nullish(),
  updatedAt: z.string().nullish(),
});

export const OrganizationProfileSchema = z.object({
  id: z.string(),
  slug: z.string(),
  name: z.string(),
  kind: z.string(), // decode defensively (handoff §3) — mapper falls back to UNKNOWN
  logoUrl: z.string().nullish(),
  bio: z.string().nullish(), // ≤ 2000 chars
  courseCount: z.number(),
  studentCount: z.number(),
  ratingAvg: z.number().nullish(), // null = no reviews (render nothing, never 0.0)
  ratingCount: z.number(),
  createdAt: z.string(),
});

export const DiscoverFeedSchema = z.object({
  featured: z.array(CatalogCourseSchema).nullish(),
  popular: z.array(CatalogCourseSchema).nullish(),
  newest: z.array(CatalogCourseSchema).nullish(),
  categories: z.array(CategorySchema).nullish(),
  featuredPrograms: z.array(ProgramSummarySchema).nullish(), // PR #184 — home banner carousel source
});

export const CohortDtoSchema = z.object({
  id: z.string().nullish(),
  courseId: z.string().nullish(),
  name: z.string().nullish(),
  priceMinor: z.number().nullish(),
  currency: z.string().nullish(),
  startAt: z.string().nullish(),
  endAt: z.string().nullish(),
  maxStudents: z.number().nullish(),
  state: z.string().nullish(),
  // Telegram course-space (PR #165): populated on GET /courses/{id} + course mutations, null on lists.
  spacePeerId: z.number().nullish(),
  spaceInviteLink: z.string().nullish(),
});

export const CatalogCourseDetailSchema = CatalogCourseSchema.extend({
  learningOutcomes: z.array(z.string()).nullish(),
  type: z.string().nullish(),
  categories: z.array(CategorySchema).nullish(),
  previewVideoUrl: z.string().nullish(),
  isEnrolled: z.boolean().nullish(),
  enrolledCohortId: z.string().nullish(),
  cohorts: z.array(CohortDtoSchema).nullish(),
});

export const SyllabusSchema = z.array(z.object({
  id: z.string(),
  title: z.string(),
  description: z.string().nullish(),
  lessons: z.array(z.object({
    id: z.string(),
    title: z.string(),
    type: z.string().nullish(),
    durationEstimateMin: z.number().nullish(),
    isCompleted: z.boolean().nullish(),
  }).loose()).nullish(),
}));

export const MyLearningSchema = z.array(z.object({
  course: CatalogCourseSchema,
  enrolledCohortId: z.string().nullish(),
  completedLessons: z.number().nullish(),
  totalLessons: z.number().nullish(),
  // Cohort's Telegram space (null until the drain marks it READY): Bot-API peer id + join-request link.
  spacePeerId: z.number().nullish(),
  spaceInviteLink: z.string().nullish(),
}));

export const EnrollmentSchema = z.object({
  id: z.string().nullish(),
  cohortId: z.string().nullish(),
  isActive: z.boolean().nullish(),
});

// ── payments / orders (Finik gateway; feature-flagged PAYMENTS_ENABLED — every route 404s when off) ──
// `status`/`provider` stay z.string() (unknown-tolerant; payments.api mapOrder narrows them, degrading an
// unrecognised value to 'UNKNOWN' — the catalog courseType / orgs kind precedent). The 2026-08 multi-rail
// overhaul (PRs #378–#403) grew OrderDto to 15 always-present fields: provider (FINIK|APPLE_APP_STORE|
// GOOGLE_PLAY), storeProductId (store rails only — null on FINIK), paymentClaimedAt (non-null = money is
// HELD for this order: keep polling, NEVER offer retry), and the deferral trio (store Ask-to-Buy holds).
// The wire contract says ALL 15 fields are always PRESENT (nullable ones as explicit null). The 6 new
// safety fields are therefore .nullable() — NOT .nullish() — so a backend that stops sending the key
// (e.g. paymentClaimedAt, the double-charge guard's input) fails the parse loudly instead of silently
// reading as "no claim". payUrl/paidAt keep the older tolerant .nullish() (pre-refresh precedent).
export const OrderSchema = z.object({
  id: z.string(),
  cohortId: z.string(),
  courseId: z.string(),
  status: z.string(),
  provider: z.string(),
  amountMinor: z.number(),
  currency: z.string(),
  payUrl: z.string().nullish(),
  storeProductId: z.string().nullable(),
  expiresAt: z.string(),
  paidAt: z.string().nullish(),
  paymentClaimedAt: z.string().nullable(),
  paymentDeferredAt: z.string().nullable(),
  paymentDeferralHoldsUntil: z.string().nullable(),
  paymentDeferralReleasedAt: z.string().nullable(),
  createdAt: z.string(),
});
export const MyOrdersSchema = z.array(OrderSchema);

// ── teacher earnings & payouts (Finik money-out; same PAYMENTS_ENABLED gate → 404 when off) ──
// Money amounts are tyiyn. `entryType`/`status`/`kind` stay z.string() (label-mapped in the view, unknown
// values degrade to the raw enum). Genuinely-optional wire fields are .nullish(); the money + id + created
// timestamps are non-null → required (drift detection). availableAt is nullish — not every ledger entry
// type carries a hold date, and a hard parse failure would blank the teacher's whole money screen.
export const EarningsSummarySchema = z.object({
  availableMinor: z.number(),
  pendingMinor: z.number(),
  lockedMinor: z.number(),
  paidOutMinor: z.number(),
  holdDays: z.number(),
});
export const LedgerEntrySchema = z.object({
  id: z.string(),
  entryType: z.string(),
  amountMinor: z.number(),
  grossMinor: z.number().nullish(),
  commissionMinor: z.number().nullish(),
  orderId: z.string().nullish(),
  availableAt: z.string().nullish(),
  createdAt: z.string(),
});
export const LedgerEntriesSchema = z.array(LedgerEntrySchema);
export const PayoutItemSchema = z.object({
  id: z.string(),
  runId: z.string().nullish(),
  amountMinor: z.number(),
  status: z.string(),
  error: z.string().nullish(),
  submittedAt: z.string().nullish(),
  settledAt: z.string().nullish(),
  createdAt: z.string(),
});
export const PayoutsSchema = z.array(PayoutItemSchema);

// Bileli: the «Вывести» press (#287) — why a SEPARATE shape from PayoutItemSchema: a request and a payout
// item have disjoint status vocabularies and independent lifecycles (the backend refused to union them,
// integration doc §1), so the client merges two arrays instead of decoding one discriminated mess.
export const PayoutRequestSchema = z.object({
  id: z.string(),
  status: z.string(), // PENDING | FULFILLED | CANCELED — plain string, unknown values degrade
  amountMinor: z.number(),
  requestedAt: z.string(),
  resolvedAt: z.string().nullish(),
  resolvedRunId: z.string().nullish(),
});
export const PayoutRequestsSchema = z.array(PayoutRequestSchema);

export const PayoutDestinationSchema = z.object({
  kind: z.string(),
  requisiteMasked: z.string(),
  verifiedName: z.string().nullish(),
  tier: z.string().nullish(),
  createdAt: z.string().nullish(),
});

// Bileli: the payable-bank catalog (GET /me/payout-services, backend round 2026-07-27) — why: a bank
// destination is only saveable with the gateway's own `serviceId`, so the picker cannot be hardcoded.
// `kind` is a plain string, not an enum: an unknown future kind must degrade to "not offered" rather
// than fail the whole parse and blank a working wallet form.
export const PayoutServiceSchema = z.object({
  serviceId: z.string(),
  kind: z.string(),
  nameRu: z.string().nullish(),
  nameKy: z.string().nullish(),
});
export const PayoutServicesSchema = z.array(PayoutServiceSchema);

export const CourseProgressSchema = z.object({ completedLessonIds: z.array(z.string()).nullish() });

// ── teacher / curriculum ──────────────────────────────────────────────────────────────────────────────
export const OrgSchema = z.object({
  id: z.string(),
  slug: z.string(),
  name: z.string(),
  kind: z.enum(['PERSONAL', 'INSTITUTIONAL']),
  createdAt: z.string(),
  role: z.string().nullish(), // caller's org role (PR #184) — absent on older backends; mapper decodes
  // Bileli 2026-07-29: SHOWROOM REMOVED (backend dropped these org fields — see the removal doc).
  // showroomStatus: z.string().nullish(), // PENDING | PROVISIONING | READY | FAILED | null
  // showroomUsername: z.string().nullish(),
  // showroomPeerId: z.string().nullish(), // '-100…' STRING — already the fork's native chat-id form
});

export const TeacherCourseDetailSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string().nullish(),
  learningOutcomes: z.array(z.string()).nullish(),
  language: z.string().nullish(),
  type: z.string().nullish(),
  state: z.string().nullish(),
  categories: z.array(CategorySchema).nullish(),
  cohorts: z.array(CohortDtoSchema).nullish(),
  level: z.string().nullish(), // Bileli: course difficulty (round 2026-07-27) — see CatalogCourseSchema
  // Bileli 2026-07-29: optional SHORT titles (≤60) the bot resolves for the Telegram topic name, so a long
  // marketing `title` stops truncating to «Ultimate AWS Certifie…». Both nullable — a course may have
  // neither, one, or both. Set via PATCH, never at create (thin create is type + title only).
  shortTitleKy: z.string().nullish(),
  shortTitleRu: z.string().nullish(),
  coverImageMediaId: z.string().nullish(),
  coverImageUrl: z.string().nullish(),
  previewVideoMediaId: z.string().nullish(),
  previewVideoUrl: z.string().nullish(),
  // Bileli 2026-07-29: SHOWROOM REMOVED (backend dropped the storefront pair — see the removal doc).
  // showroomUsername: z.string().nullish(),
  // showroomTopicId: z.number().nullish(),
});

export const QuestionDtoSchema = z.object({
  id: z.string(),
  courseId: z.string(),
  type: z.enum(['MULTIPLE_CHOICE', 'TRUE_FALSE', 'SHORT_ANSWER']),
  stem: z.string(),
  contentJson: z.record(z.string(), z.unknown()),
  explanationText: z.string().nullish(),
  relatedLessonId: z.string().nullish(),
  relatedTimestampSec: z.number().nullish(),
  tags: z.array(z.string()).nullish(),
});

export const TeacherLessonSchema = z.object({
  id: z.string(),
  moduleId: z.string().nullish(),
  orderIndex: z.number().nullish(),
  title: z.string(),
  type: z.string().nullish(),
  isCompleted: z.boolean().nullish(),
  durationEstimateMin: z.number().nullish(),
  description: z.string().nullish(),
  contentJson: z.object({
    body: z.string().nullish(),
    pdfMediaId: z.string().nullish(),
    pdfUrl: z.string().nullish(),
    videoMediaId: z.string().nullish(),
    videoUrl: z.string().nullish(),
  }).loose().nullish(),
  questions: z.array(QuestionDtoSchema).nullish(),
});

export const TeacherModuleSchema = z.object({
  id: z.string(),
  courseId: z.string(),
  orderIndex: z.number(),
  title: z.string(),
  description: z.string().nullish(),
  lessons: z.array(TeacherLessonSchema).nullish(),
});

export const TeacherModulesSchema = z.array(TeacherModuleSchema);
export const OrgsSchema = z.array(OrgSchema);
export const OrgCoursesSchema = z.array(CatalogCourseSchema); // GET /orgs/{slug}/courses (provider page)

// ── org invite links + roster (backend PRs #220/#222) ────────────────────────────────────────────────
// Single-use invites, 7-day expiry, revocable; the raw token exists ONLY in the mint response. A
// membership is always a real user (no invitedPhone/isPending — the phone-keyed flow died in V54).
export const OrgInviteSchema = z.object({
  id: z.string(),
  orgId: z.string(),
  role: z.string(), // OWNER | ADMIN | TEACHER | INSTRUCTOR — decoded defensively (unknown roles degrade)
  createdAt: z.string(),
  expiresAt: z.string(),
});
export const OrgInvitesSchema = z.array(OrgInviteSchema);
export const OrgInviteMintSchema = z.object({
  invite: OrgInviteSchema,
  token: z.string(), // shown once — never persisted anywhere
});
export const OrgMemberSchema = z.object({
  id: z.string(),
  orgId: z.string(),
  userId: z.string(),
  role: z.string(),
  createdAt: z.string(),
  // Bileli: identity, requested in docs/backend/TASK-org-members-identity.md and parsed BEFORE it
  // ships — the same trick the roster used for telegramUsername. The «Команда» rows fall back to a
  // short user id today and light up on their own the moment the backend starts sending these, with
  // no second deploy on our side.
  displayName: z.string().nullish(),
  telegramUsername: z.string().nullish(),
  avatarUrl: z.string().nullish(),
});
export const OrgMembersSchema = z.array(OrgMemberSchema);
export const OrgInvitePreviewSchema = z.object({
  org: OrgSchema,
  role: z.string(),
  expiresAt: z.string(),
});
export const OrgInviteAcceptSchema = z.object({
  org: OrgSchema,
  member: OrgMemberSchema,
  alreadyMember: z.boolean(),
});
export const QuestionsSchema = z.array(QuestionDtoSchema);

/** The one question type shared across teach + learn (inferred, so it can't drift from the schema). */
export type QuestionDto = z.infer<typeof QuestionDtoSchema>;

// ── student quiz attempts (POST /attempts · GET /me/lessons/{id}/attempts) ──
// wasCorrect/responseJson/lessonAutoCompleted/xpAwarded/currentStreak are Kotlin non-nullable —
// kept REQUIRED on purpose (drift detection), unlike the blanket-.nullish() display fields.
export const AttemptDtoSchema = z.object({
  id: z.string(),
  questionId: z.string(),
  lessonId: z.string().nullish(),
  attemptedAt: z.string().nullish(),
  wasCorrect: z.boolean(),
  responseJson: z.record(z.string(), z.unknown()),
});

export const AttemptResultSchema = z.object({
  attempt: AttemptDtoSchema,
  question: QuestionDtoSchema, // teacher-mode: answer key + explanation revealed after answering
  relatedLesson: z.object({ id: z.string(), title: z.string() }).loose().nullish(),
  relatedTimestampSec: z.number().nullish(),
  lessonAutoCompleted: z.boolean(),
  xpAwarded: z.number(),
  currentStreak: z.number(),
  nextDueAt: z.string().nullish(), // PR #165: next SRS review; null when SRS state unavailable
});

export const MyQuizAttemptsSchema = z.object({
  lessonId: z.string().nullish(),
  questions: z.array(z.object({
    question: QuestionDtoSchema,
    attempts: z.array(AttemptDtoSchema).nullish(), // newest-first; empty/absent = unattempted
  })).nullish(),
});

// ── course reviews (GET/POST /courses/{id}/reviews · GET /me/courses/{id}/review) ──
const ReviewAuthorSchema = z.object({
  userId: z.string(),
  displayName: z.string().nullish(),
  avatarUrl: z.string().nullish(),
  telegramUsername: z.string().nullish(), // public since feat/telegram-profile-sync
});
export const ReviewSchema = z.object({
  id: z.string(),
  courseId: z.string().nullish(),
  author: ReviewAuthorSchema,
  rating: z.number(), // 1–5
  comment: z.string().nullish(),
  verified: z.boolean().nullish(),
  createdAt: z.string().nullish(),
  updatedAt: z.string().nullish(),
});
export const ReviewsPageSchema = z.object({
  items: z.array(ReviewSchema).nullish(),
  nextCursor: z.string().nullish(),
});

// ── per-lesson Q&A discussions (GET/POST /lessons/{id}/discussions · /discussions/{id}[/replies]) ──
const DiscussionAuthorSchema = z.object({
  userId: z.string(),
  displayName: z.string().nullish(),
  avatarUrl: z.string().nullish(),
  telegramUsername: z.string().nullish(), // public since feat/telegram-profile-sync
});
export const DiscussionReplySchema = z.object({
  id: z.string(),
  discussionId: z.string().nullish(),
  author: DiscussionAuthorSchema,
  body: z.string(),
  isFromTeacher: z.boolean().nullish(),
  createdAt: z.string().nullish(),
  updatedAt: z.string().nullish(),
});
export const DiscussionSchema = z.object({
  id: z.string(),
  lessonId: z.string().nullish(),
  courseId: z.string().nullish(),
  author: DiscussionAuthorSchema,
  body: z.string(),
  isLocked: z.boolean().nullish(),
  isAnswered: z.boolean().nullish(),
  replyCount: z.number().nullish(),
  teacherReply: DiscussionReplySchema.nullish(), // latest teacher reply, list view only
  createdAt: z.string().nullish(),
  updatedAt: z.string().nullish(),
});
export const DiscussionsPageSchema = z.object({
  items: z.array(DiscussionSchema).nullish(),
  nextCursor: z.string().nullish(),
});
export const DiscussionThreadSchema = z.object({
  discussion: DiscussionSchema,
  replies: z.array(DiscussionReplySchema).nullish(),
});

// Teacher inbox (GET /courses/{id}/discussions) — a discussion enriched with its lesson/module context.
// `telegramUsername` is a TEACHER-ONLY handle for the "написать в Telegram" DM button — absent until the
// backend exposes it (see docs/backend/TASK-expose-telegram-username-for-teacher-dm.md); `.nullish()` so
// the button just stays hidden until then.
export const CourseDiscussionSchema = DiscussionSchema.extend({
  lessonTitle: z.string().nullish(),
  moduleId: z.string().nullish(),
  moduleTitle: z.string().nullish(),
  telegramUsername: z.string().nullish(),
});
export const CourseDiscussionsPageSchema = z.object({
  items: z.array(CourseDiscussionSchema).nullish(),
  nextCursor: z.string().nullish(),
  unansweredCount: z.number().nullish(),
});

/** POST /courses/{id}/complete — the set-once completion milestone (celebrate iff firstCompletion). */
export const CourseCompletionSchema = z.object({
  allComplete: z.boolean(),
  firstCompletion: z.boolean(),
  completedAt: z.string().nullish(),
  xpAwarded: z.number().nullish(), // PR #165: +100 first completion, else 0
});

/** GET /me/stats — gamification aggregates (zeroes when the user never practiced). */
export const UserStatsSchema = z.object({
  currentStreak: z.number(),
  longestStreak: z.number(),
  totalXp: z.number(),
  lastStreakDay: z.string().nullish(),
});
